import React, { JSX, ReactNode, useMemo } from 'react';
import { useTheme, Box } from '@mui/material';
import { useTranslation } from "react-i18next";
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleWidgetResponseByName, getWidgetResponsesByName, widgetQueryTitleByResponses, WidgetResponseItem } from 'system/widget/Widget';
import { defaultSettings, SubqueriesSettingsStateType } from './SubqueriesSettings';
import { useTemplateQueries } from 'routes/dashboard/DashboardPageRoot';
import SubqueryRow from './SubqueryRow';

function SubqueriesContent(props: WithQueryWidgetProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const templateQueriesContext = useTemplateQueries();
  const widgetQueries = props.widget?.request_info?.queries || [];

  const hasQueries = templateQueriesContext?.selectedTemplateQueries?.length > 0 || widgetQueries?.length > 0;

  const currentState = {...defaultSettings, ...props.widget?.data?.settings};

  const widgetSettings:SubqueriesSettingsStateType = props.widget?.data?.settings || defaultSettings;

  const displayRows: ReactNode[] = useMemo(() => {
    let sovResponses = getWidgetResponsesByName(props.apiResponses, "subqueries");
    const rootApiResponse = getSingleWidgetResponseByName(props.apiResponses, "subqueries-root");

    const rows: ReactNode[] = [];

    if (sovResponses && !Array.isArray(sovResponses)) {
      sovResponses = [sovResponses];
    }

    sovResponses?.forEach((response: WidgetResponseItem) => {
      const positiveBucket = response.responseBody?.sentiment?._buckets?.find((bucket: any) => bucket?._key === "p");
      const negativeBucket = response.responseBody?.sentiment?._buckets?.find((bucket: any) => bucket?._key === "n");
      const positive = positiveBucket?._count || 0;
      const negative = negativeBucket?._count || 0;

      const positivity = positive / ((positive + negative) || 1);

      response.responseBody.subqueryTotal = response.responseBody?.histogram?._buckets?.reduce((total: number, bucket: any) => total + (bucket?.social_universe || 0), 0);
      response.responseBody.positivity = positivity;
    });

    let sortField = widgetSettings.volumeField === "social_universe" ? "subqueryTotal" : "_total";
    if (widgetSettings.sorting === "sentiment") {
      sortField = "positivity";
    }

    if (widgetSettings.sorting !== "queryOrder") {
      sovResponses = sovResponses?.sort((a: WidgetResponseItem, b: WidgetResponseItem) => {
        const aTotal = a.responseBody?.[sortField] || 0;
        const bTotal = b.responseBody?.[sortField] || 0;
        return bTotal - aTotal;
      });
    }

    if (Array.isArray(sovResponses) && sovResponses.length > 0 && rootApiResponse) {
      sovResponses.forEach((response, index) => {
        rows.push(
          <SubqueryRow
            key={`query-${index}`}
            widgetSettings={widgetSettings}
            rowIndex={index}
            rowResponse={response}
            rootResponse={rootApiResponse}
          />
        );
      });
    }
    return rows;
  }, [theme.palette.primary.main, props.apiResponses]);

  const widgetQueryTitle = widgetQueryTitleByResponses(props.apiResponses, "subqueries-root", i18n);

  return (<>
{displayRows.length > 0 &&
  <WidgetLayout
    header={
      <WidgetTitle
        style={{ backgroundColor: theme.palette.background.paper, borderBottom: `1px solid ${theme.palette.divider}`, pb: 1 }}
        alignment={currentState.widgetTitleAlignment}
        customTitle={currentState.customWidgetTitle}
        defaultTitle={i18n.t('widget-types.subqueries.label')}
        settingsTitle={currentState.widgetTitle}
        widgetQueryTitle={widgetQueryTitle}
        /> }>
    <Box
      className="drag-cancel"
      sx={{
        overflowY: "auto",
        minHeight: "100%",
        backgroundColor: theme.palette.background.default
      }}>
      {displayRows}
    </Box>
  </WidgetLayout>
}
{!displayRows?.length && !!hasQueries &&
  <WidgetAlert type={WidgetErrorType.NO_DATA} />
}
</>);
}

export default withQueryWidget(SubqueriesContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
